import { useState } from 'react'
import { supabase } from '../../lib/supabase'
import { Upload, X, FileIcon, ImageIcon, VideoIcon } from 'lucide-react'
import { useToast } from '../../contexts/ToastContext'

interface FileUploaderProps {
  courseId: string
  onFileUploaded: (url: string) => void
  acceptedTypes?: string
  maxSizeMB?: number
}

export default function FileUploader({
  courseId,
  onFileUploaded,
  acceptedTypes = 'image/*,video/*,application/pdf',
  maxSizeMB = 100
}: FileUploaderProps) {
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const { showToast } = useToast()

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validar tamaño
    const maxSizeBytes = maxSizeMB * 1024 * 1024
    if (file.size > maxSizeBytes) {
      showToast('error', `El archivo excede el tamaño máximo de ${maxSizeMB}MB`)
      return
    }

    setUploading(true)
    setUploadProgress(0)

    try {
      // Generar nombre único
      const fileExt = file.name.split('.').pop()
      const fileName = `${courseId}/${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`

      // Subir a Supabase Storage
      const { data, error } = await supabase.storage
        .from('course-content')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        })

      if (error) throw error

      // Obtener URL pública
      const { data: urlData } = supabase.storage
        .from('course-content')
        .getPublicUrl(data.path)

      showToast('success', 'Archivo subido exitosamente')
      onFileUploaded(urlData.publicUrl)
      setUploadProgress(100)
    } catch (error: any) {
      console.error('Error uploading file:', error)
      const errorMessage = 'Error al subir el archivo: ' + error.message
      showToast('error', errorMessage)
    } finally {
      setUploading(false)
      setTimeout(() => setUploadProgress(0), 1000)
    }
  }

  const getFileIcon = (type: string) => {
    if (type.startsWith('image/')) return <ImageIcon className="h-5 w-5" />
    if (type.startsWith('video/')) return <VideoIcon className="h-5 w-5" />
    return <FileIcon className="h-5 w-5" />
  }

  return (
    <div className="space-y-2">
      <label className="flex items-center justify-center px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-education hover:bg-education/5 transition-colors">
        <input
          type="file"
          className="hidden"
          accept={acceptedTypes}
          onChange={handleFileUpload}
          disabled={uploading}
        />
        <Upload className="h-5 w-5 text-gray-500 mr-2" />
        <span className="text-sm text-gray-600">
          {uploading ? 'Subiendo archivo...' : 'Subir archivo'}
        </span>
      </label>

      {uploading && (
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-education h-2 rounded-full transition-all duration-300"
            style={{ width: `${uploadProgress}%` }}
          />
        </div>
      )}

      <p className="text-xs text-gray-500">
        Formatos aceptados: imágenes, videos, PDF. Máximo {maxSizeMB}MB
      </p>
    </div>
  )
}
